// "Upload a folder" source mode (DEC-180, UI_SDD §4.1).
//
// Clicking the field opens the native folder picker. Picking reads the file list and
// sends nothing; everything below the field is computed in the browser. What is left out
// is always listed, never collapsed (§0.6).

import { useRef } from 'react'
import { FolderOpen } from 'lucide-react'
import type { ScanSourcesResponse } from '../../types/api'
import { filesLabel, formatBytes, previewProblem, type FolderPreview } from '../../utils/folderUpload'

interface Props {
  limits: ScanSourcesResponse['upload'] | null
  preview: FolderPreview | null
  includeGit: boolean
  disabled: boolean
  onPick: (files: File[]) => void
  onIncludeGitChange: (value: boolean) => void
}

export function FolderUploadField({
  limits,
  preview,
  includeGit,
  disabled,
  onPick,
  onIncludeGitChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const problem = previewProblem(preview, limits, includeGit)

  return (
    <div>
      <label className="block text-[13px] font-medium text-body mb-1.5" htmlFor="folder-upload">
        Folder
      </label>
      {/* The real input is hidden and the styled button opens it: a file input cannot be
          styled as a field, and the founder's expectation was that clicking the field
          itself opens Finder. */}
      <input
        id="folder-upload"
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid="folder-input"
        // Non-standard but supported by every current browser; React passes it through.
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        multiple
        disabled={disabled}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? [])
          if (picked.length) onPick(picked)
          // Allow picking the same folder again after a change on disk.
          e.target.value = ''
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="w-full flex items-center gap-2 border border-control rounded-md px-3 py-2 text-left text-[14px] text-ink focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
      >
        <FolderOpen className="w-4 h-4 text-muted shrink-0" />
        <span className={preview ? 'truncate' : 'text-faint'}>
          {preview ? preview.folderName : 'Choose a folder...'}
        </span>
      </button>

      {!preview && (
        <p className="text-[12px] text-faint mt-1.5">
          Nothing is sent until you click Scan folder.
          {limits ? ` Up to ${limits.max_upload_mb} MB after compression.` : ''}
        </p>
      )}

      {preview && (
        <div className="mt-2 space-y-2 text-[12px] leading-[1.55] text-body" data-testid="folder-preview">
          <p>
            <span className="font-medium">{preview.folderName}</span> ·{' '}
            {filesLabel(preview.fileCount)} ·{' '}
            {formatBytes(preview.bytes + (includeGit ? preview.gitBytes : 0))} to compress and upload
          </p>

          {preview.excluded.length > 0 ? (
            <p className="text-muted">
              Left out:{' '}
              {preview.excluded
                .map((e) => `${e.path} (${filesLabel(e.file_count)})`)
                .join(', ')}
            </p>
          ) : (
            <p className="text-muted">Nothing left out.</p>
          )}

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={includeGit}
              disabled={disabled}
              onChange={(e) => onIncludeGitChange(e.target.checked)}
              className="mt-[3px]"
            />
            <span>
              Include git history
              {preview.hasGit ? ` (adds ${formatBytes(preview.gitBytes)})` : ''}. Needed for code age and
              ownership.
            </span>
          </label>
          {(!includeGit || !preview.hasGit) && (
            <p className="text-muted">
              {!preview.hasGit
                ? 'This folder has no git history we can read, so code age and ownership will not be measured.'
                : 'Without git history, code age and ownership will not be measured.'}
            </p>
          )}

          <p className="text-muted">
            Compared with earlier uploads of a folder named "{preview.folderName}" in this workspace.
          </p>

          {problem && <p className="text-critical">{problem}</p>}
        </div>
      )}
    </div>
  )
}
